/**
 * Surprise Boxes — data model + pure drop engine.
 *
 * A box, when opened, rolls ONE cosmetic the user does not already own, weighted
 * by the box's rarity profile. No-duplicate rolls, dust on a dead pool, and a
 * pity timer keep opens feeling good. See CHALLENGES_SYSTEM.md §2.
 *
 * This module is pure (no Firestore). The service layer (StoreStateService)
 * wires it to points spend/award and StoreSettings persistence.
 */

import type { Rarity } from './rarity';
import { CURSOR_BUNDLES } from './cursorBundles';
import { AVATAR_FRAMES } from './avatarFrames';
import { FINISH_EFFECTS } from './finishEffects';
import { PREMIUM_AVATARS } from './premiumAvatars';
import { BACKGROUNDS } from './backgrounds';
import { AVATAR_GADGETS } from './avatarGadgets';
import {
  getEarnedPresetAvatars,
  presetAvatarRarity,
  getEarnedGradients,
  gradientRarity,
} from '../constants/avatars';

export type BoxType = 'common' | 'rare' | 'epic' | 'legendary';

// 'presetAvatar' = earned emoji avatars; 'background' = earned avatar gradients.
export type CosmeticCategory = 'cursor' | 'frame' | 'finishEffect' | 'premiumAvatar' | 'background' | 'gadget' | 'presetAvatar';

/** Rarity axis in ascending order. */
export const RARITIES: Rarity[] = ['common', 'rare', 'epic', 'legendary'];

export interface BoxDefinition {
  type: BoxType;
  /** Buyable price in points; null = earned-only (never purchasable). */
  price: number | null;
  label: string;
  description: string;
  /** Relative drop weight per rarity. Filtered to unowned, then normalized. */
  weights: Record<Rarity, number>;
  /** Points refunded when the entire reachable pool is already owned. */
  dustValue: number;
}

export const BOX_DEFINITIONS: Record<BoxType, BoxDefinition> = {
  common: {
    type: 'common',
    price: 40,
    label: 'Common Box',
    description: 'Mostly common cosmetics, with a small chance at something rarer.',
    weights: { common: 80, rare: 20, epic: 0, legendary: 0 },
    dustValue: 20,
  },
  rare: {
    type: 'rare',
    price: 90,
    label: 'Rare Box',
    description: 'Rare-weighted, with a real shot at an Epic drop.',
    weights: { common: 25, rare: 55, epic: 18, legendary: 2 },
    dustValue: 45,
  },
  epic: {
    type: 'epic',
    price: null, // earned only
    label: 'Epic Box',
    description: 'Epic-weighted, with a chance at a Legendary.',
    weights: { common: 0, rare: 20, epic: 65, legendary: 15 },
    dustValue: 80,
  },
  legendary: {
    type: 'legendary',
    price: null, // earned only
    label: 'Legendary Box',
    description: 'Guaranteed Epic or better, with a strong Legendary chance.',
    weights: { common: 0, rare: 0, epic: 60, legendary: 40 },
    dustValue: 150,
  },
};

/** Guaranteed Rare-or-better at least once every PITY_THRESHOLD opens. */
export const PITY_THRESHOLD = 5;

export interface PoolItem {
  category: CosmeticCategory;
  id: string;
  rarity: Rarity;
  name: string;
}

/**
 * The full set of ownable cosmetics across every catalog, excluding the free
 * defaults. Built once at module load — catalogs are static.
 */
export function buildCosmeticPool(): PoolItem[] {
  const pool: PoolItem[] = [];
  for (const c of CURSOR_BUNDLES) {
    if (c.isDefault) continue;
    pool.push({ category: 'cursor', id: c.id, rarity: c.rarity, name: c.name });
  }
  for (const f of AVATAR_FRAMES) {
    pool.push({ category: 'frame', id: f.id, rarity: f.rarity, name: f.name });
  }
  for (const e of FINISH_EFFECTS) {
    if (e.isDefault) continue;
    pool.push({ category: 'finishEffect', id: e.id, rarity: e.rarity, name: e.name });
  }
  for (const a of PREMIUM_AVATARS) {
    pool.push({ category: 'premiumAvatar', id: a.id, rarity: a.rarity, name: a.name });
  }
  for (const b of BACKGROUNDS) {
    pool.push({ category: 'background', id: b.id, rarity: b.rarity, name: b.name });
  }
  for (const g of AVATAR_GADGETS) {
    pool.push({ category: 'gadget', id: g.id, rarity: g.rarity, name: g.name });
  }
  // Earned emoji avatars (animals / icons / symbols) and colourful gradients.
  for (const a of getEarnedPresetAvatars()) {
    pool.push({ category: 'presetAvatar', id: a.id, rarity: presetAvatarRarity(a), name: a.name });
  }
  for (const g of getEarnedGradients()) {
    pool.push({ category: 'background', id: g.id, rarity: gradientRarity(g.id), name: g.name });
  }
  return pool;
}

export type RollResult =
  | { kind: 'item'; item: PoolItem; pity: number }
  | { kind: 'dust'; points: number; pity: number };

function weightedPickRarity(
  candidates: Rarity[],
  weights: Record<Rarity, number>,
  rng: () => number,
): Rarity {
  const total = candidates.reduce((sum, r) => sum + weights[r], 0);
  let roll = rng() * total;
  for (const r of candidates) {
    roll -= weights[r];
    if (roll < 0) return r;
  }
  return candidates[candidates.length - 1];
}

/**
 * Pure roll. Given the box, the set of already-owned cosmetic IDs, and the
 * current pity counter, returns the dropped item (or dust) and the next pity
 * counter. `rng` is injectable for testing; defaults to Math.random.
 */
export function rollBox(
  boxType: BoxType,
  ownedIds: Set<string>,
  pity: number,
  rng: () => number = Math.random,
): RollResult {
  const def = BOX_DEFINITIONS[boxType];
  const unowned = buildCosmeticPool().filter(p => !ownedIds.has(p.id));

  const byRarity = {} as Record<Rarity, PoolItem[]>;
  for (const r of RARITIES) byRarity[r] = [];
  for (const p of unowned) byRarity[p.rarity].push(p);

  // Whole reachable pool exhausted → dust.
  const anyUnowned = RARITIES.filter(r => byRarity[r].length > 0);
  if (anyUnowned.length === 0) {
    return { kind: 'dust', points: def.dustValue, pity: 0 };
  }

  // Pity: if this open would hit the threshold, force a Rare+ drop.
  const pityTriggers = pity + 1 >= PITY_THRESHOLD;
  const weights = { ...def.weights };
  if (pityTriggers) weights.common = 0;

  // Candidate rarities: positive weight AND at least one unowned item.
  let candidates = RARITIES.filter(r => weights[r] > 0 && byRarity[r].length > 0);

  // The box's weighted bands are all exhausted (e.g. all commons owned). Bump
  // to the lowest available rarity so the box still drops *something* real.
  if (candidates.length === 0) candidates = [anyUnowned[0]];

  const chosenRarity = candidates.length === 1
    ? candidates[0]
    : weightedPickRarity(candidates, weights, rng);

  const bucket = byRarity[chosenRarity];
  const item = bucket[Math.floor(rng() * bucket.length)];
  const nextPity = chosenRarity === 'common' ? pity + 1 : 0;
  return { kind: 'item', item, pity: nextPity };
}

export function getBoxDefinition(type: BoxType): BoxDefinition {
  return BOX_DEFINITIONS[type];
}

/** Buyable boxes, in display order. */
export const BUYABLE_BOXES: BoxDefinition[] = [BOX_DEFINITIONS.common, BOX_DEFINITIONS.rare];
